import { dateToLabel } from 'further-ui/utils';
import PageContainer from 'components/PageContainer';
import Table, { TableActions } from 'components/Table';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import AlertDialog from 'components/AlertDialog';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import PageContent from 'components/PageContent';
import PageFilters, { Filter } from 'components/PageFilters';
import api from 'lib/trpcClient';
import {
  PERFORMANCE_FEE_TEMPLATE_ADD,
  createPerformanceFeeTemplateEditRoute,
} from 'adminConstants/routes';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useHistory } from 'react-router-dom';
import TextField from 'components/FormElements/AppTextInput';
import { FirmSelector } from 'components/FirmSelector';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type PerformanceFeeTemplate = {
  _id: string;
  name: string;
  createdAt?: string;
  firmName?: string;
  templateInUse?: boolean;
};

const pageId = 'performance-fee-templates';

const PerformanceFeeTemplates: React.FC = () => {
  const deleteDisclosure = useDisclosure<{ templateId: string }>();
  const { isSuperAdmin } = useGetRole();
  const history = useHistory();
  const [canCreatePerformanceFeeTemplate, canDeletePerformanceFeeTemplate] =
    useHasPermissions([
      RolePermission.CreatePerformanceFees,
      RolePermission.DeletePerformanceFees,
    ]);

  const pagination = usePagination({
    orderBy: 'createdAt',
    order: 'desc',
    id: pageId,
  });

  const defaultFilters = {
    searchByName: '',
  };

  const { filters, handleChangeFilters } = useFilters<{
    firmId?: string;
    searchByName: string;
  }>(pageId, {
    defaultFilters,
    onFiltersChange: pagination.toFirstPage,
  });

  const {
    data: performanceFeeTemplates,
    isLoading: performanceFeeTemplatesLoading,
    isFetching: performanceFeeTemplatesFetching,
  } = api.performanceFeeTemplates.list.useQuery({
    ...pagination.properties,
    firmId: filters.firmId,
    name: filters.searchByName,
  });

  const {
    mutate: deletePerformanceFeeTemplate,
    isPending: deletePerformanceFeeTemplatePending,
  } = api.performanceFeeTemplates.remove.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Performance fee template deleted successfully',
      onSuccess: () => {
        pagination.toFirstPage();
        deleteDisclosure.onClose();
      },
    }),
  );

  const handleConfirmDelete = () => {
    deletePerformanceFeeTemplate({
      id: deleteDisclosure.actionProps?.templateId,
    });
  };

  const columns = [
    ...(isSuperAdmin
      ? [
          {
            label: 'Firm',
            key: 'firm',
            render: (row: PerformanceFeeTemplate) => row.firmName,
          },
        ]
      : []),
    {
      label: 'Name',
      key: 'name',
    },
    {
      label: 'Date created',
      key: 'createdAt',
      render: (row: PerformanceFeeTemplate) => dateToLabel(row.createdAt),
    },
    {
      label: 'Actions',
      key: 'actions',
      sort: false,
      render: (row: PerformanceFeeTemplate) => (
        <TableActions
          showAsDropdown
          className="flex flex-row justify-center"
          actions={[
            {
              label: canCreatePerformanceFeeTemplate ? 'Edit' : 'View',
              icon: canCreatePerformanceFeeTemplate ? Edit : Visibility,
              color: 'primary',
              onClick: () => {
                history.push(createPerformanceFeeTemplateEditRoute(row._id));
              },
            },
            {
              label: 'Delete',
              icon: Delete,
              color: 'error',
              visible: canDeletePerformanceFeeTemplate,
              disabled: row.templateInUse,
              tooltip: row.templateInUse
                ? 'It is not possible to delete a fee preset that is currently in use.'
                : undefined,
              onClick: () => {
                deleteDisclosure.stageAction({ templateId: row._id });
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageContainer heading="Fees: Performance fee templates">
      <PageContent>
        <PageFilters
          pageId={pageId}
          buttons={[
            {
              link: PERFORMANCE_FEE_TEMPLATE_ADD,
              label: 'Create new template',
              visible: canCreatePerformanceFeeTemplate,
            },
          ]}
        >
          {isSuperAdmin && (
            <Filter>
              <FirmSelector
                firmId={filters.firmId}
                placeholder="Search by firm"
                onChange={(firmId) => {
                  handleChangeFilters({ firmId });
                }}
              />
            </Filter>
          )}
          <Filter>
            <TextField
              type="search"
              name="searchByName"
              placeholder="Search by template name"
              fullWidth
              value={filters.searchByName}
              onChange={(event) => {
                handleChangeFilters({
                  searchByName: event.target.value,
                });
              }}
            />
          </Filter>
        </PageFilters>
        <Table
          loading={
            performanceFeeTemplatesLoading || performanceFeeTemplatesFetching
          }
          columns={columns}
          tablebody={performanceFeeTemplates?.result ?? []}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          count={performanceFeeTemplates?.total ?? 0}
          pagination={true}
          variant="nohover"
          emptyMessage={'No performance fee templates found'}
        />

        <AlertDialog
          onClose={deleteDisclosure.onClose}
          onConfirm={handleConfirmDelete}
          open={deleteDisclosure.isOpen}
          title="Confirm delete"
          content={<p>Are you sure you wish to delete this template?</p>}
          confirmBtnProps={{
            loading: deletePerformanceFeeTemplatePending,
          }}
          cancelBtnProps={{ variant: 'outlined', color: 'primary' }}
        />
      </PageContent>
    </PageContainer>
  );
};

export default PerformanceFeeTemplates;
